import React, { useEffect, useState } from "react";
import { Form, Input, Button, message } from "antd";
import { UserOutlined, LockOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import "aos/dist/aos.css";
import AOS from "aos";
import OtpVerification from "../components/OtpVerification";
import "./LoginForm.css";

const LoginForm = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [requiresOtp, setRequiresOtp] = useState(false);
  const [uid, setUid] = useState("");
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [credentials, setCredentials] = useState(null);
  const [resendCount, setResendCount] = useState(0);

  useEffect(() => {
    AOS.init();
  }, []);

  const onFinish = async (values) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await API.post("/login", values);
      if (res.data.requiresOtp) {
        setRequiresOtp(true);
        setUid(values.uid);
        setCredentials({ uid: values.uid, password: values.password });
        messageApi.info("OTP sent to your registered email.");
      } else {
        handleLoginSuccess(res.data);
      }
    } catch (error) {
      messageApi.error(error.response?.data?.error || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const onOtpSubmit = async (values) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await API.post("/login/verify-otp", {
        uid,
        otp: values.otp,
      });
      handleLoginSuccess(res.data);
    } catch (error) {
      messageApi.error(
        error.response?.data?.error || "OTP verification failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
    form.setFieldsValue({ otp: value });
  };

  const handleOtpVerify = () => {
    if (loading) return;
    if (otp.length !== 6) {
      messageApi.warning("Please enter the complete 6-digit OTP.");
      return;
    }
    form.submit();
  };

  const handleResend = async () => {
    if (loading || !credentials) return;
    setLoading(true);
    try {
      const res = await API.post("/login", credentials);
      if (res.data.requiresOtp) {
        setResendCount((c) => c + 1);
        messageApi.info("OTP resent to your registered email.");
      } else {
        handleLoginSuccess(res.data);
      }
    } catch (error) {
      messageApi.error(error.response?.data?.error || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (data) => {
    messageApi.success(data.message);

    localStorage.setItem("name", data.name);
    sessionStorage.setItem("show_ttl_popup", "1");
    localStorage.setItem("role", data.role);
    localStorage.setItem("enrollment", data.uid);
    localStorage.setItem("token", data.token);
    localStorage.removeItem("theme");

    switch (data.role) {
      case "user":
        navigate("/user-order");
        break;
      case "manager":
        navigate("/manager-dash");
        break;
      case "admin":
        navigate("/admin-dash");
        break;
      case "dispatch":
        navigate("/dispatch-dash");
        break;
      case "supervisor":
        navigate("/supervisor-dash");
        break;
      case "shippingmanager":
        navigate("/shippingmanager-dash");
        break;
      case "accountant":
        navigate("/accountant-dash");
        break;
      default:
        navigate("/");
    }
  };

  return requiresOtp ? (
    <div
      style={{
        backgroundImage:
          'url("https://img.freepik.com/free-vector/paper-style-white-monochrome-background_52683-66443.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
        {contextHolder}
        <Form
          form={form}
          name="login_form"
          className="w-full max-w-[400px]"
          onFinish={onOtpSubmit}
        >
          <OtpVerification
            loading={loading}
            otp={otp}
            onOtpChange={handleOtpChange}
            onVerify={handleOtpVerify}
            onResend={handleResend}
            resetKey={resendCount}
          />
          <Form.Item
            name="otp"
            className="hidden"
            rules={[{ required: true, message: "Please enter the OTP!" }]}
          >
            <Input />
          </Form.Item>
        </Form>
    </div>
  ) : (
    <div
      style={{
        backgroundImage:
          'url("https://img.freepik.com/free-vector/paper-style-white-monochrome-background_52683-66443.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div className="login-container" data-aos="fade-up">
        {contextHolder}
        <Form
          form={form}
          name="login_form"
          className="login-form"
          onFinish={onFinish}
        >
          <div className="login-logo">
            <img src="./logo.png" alt="Logo" />
          </div>
          <Form.Item
            name="uid"
            rules={[{ required: true, message: "Please input your UID!" }]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="UID"
              disabled={loading}
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Please input your password!" }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              disabled={loading}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login-form-button"
              style={{ backgroundColor: "rgb(71,178,228)" }}
              loading={loading}
              disabled={loading}
            >
              {loading ? "Signing In..." : "Log in"}
            </Button>
          </Form.Item>
        </Form>
        <div className="login-footer">
          <p>
            By logging in you agree to our <a href="/">privacy policy</a> &{" "}
            <a href="/">terms of service</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
